import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Sparkles } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { WaitlistFormCore } from "@/components/landing/WaitlistFormCore";
import { fetchStory } from "@/lib/api";

function applyJournalPostSEO(story) {
  if (!story) return;
    const prefix = story.url_prefix || "/journal";
    const url = `https://supple.care${prefix}/${story.slug}`;
    const title = `${story.title} | Supple Journal`;
    document.title = title.length > 62 ? `${story.title} | Supple` : title;

    const setMeta = (attr, name, content) => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("name", "description", story.excerpt?.slice(0, 155) || "");
    setMeta("property", "og:title", story.title);
    setMeta("property", "og:description", story.excerpt?.slice(0, 200) || "");
    setMeta("property", "og:url", url);
    setMeta("property", "og:type", "article");
    setMeta(
      "property",
      "og:image",
      story.hero_image?.startsWith("http")
        ? story.hero_image
        : `https://supple.care${story.hero_image}`
    );

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    // JSON-LD — MedicalWebPage + BlogPosting
    const graph = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": ["BlogPosting", "MedicalWebPage"],
          "@id": url,
          headline: story.title,
          url,
          description: story.excerpt,
          image: story.hero_image?.startsWith("http")
            ? story.hero_image
            : `https://supple.care${story.hero_image}`,
          datePublished: story.published_at,
          dateModified: story.published_at,
          author: { "@type": "Person", name: "Shruti Gupta" },
          publisher: {
            "@type": "Organization",
            name: "Supple",
            logo: { "@type": "ImageObject", url: "https://supple.care/brand/supple-logo.png" },
          },
          about: {
            "@type": "MedicalCondition",
            name: "Perimenopause",
          },
          audience: {
            "@type": "MedicalAudience",
            audienceType: "Women aged 35-55, primarily Asian women",
          },
        },
      ],
    };
    let script = document.getElementById("journal-post-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "journal-post-jsonld";
      document.head.appendChild(script);
    }
  script.textContent = JSON.stringify(graph);
}

function useJournalPostSEO(story) {
  useEffect(() => applyJournalPostSEO(story), [story]);
}

export default function JournalPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useJournalPostSEO(story);

  useEffect(() => {
    let active = true;
    setStory(null);
    setNotFound(false);
    fetchStory(slug)
      .then((data) => {
        if (active) setStory(data);
      })
      .catch(() => {
        if (active) setNotFound(true);
      });
    return () => {
      active = false;
    };
  }, [setNotFound, setStory, slug]);

  if (notFound) {
    return (
      <div className="bg-bg text-[#1C1917] min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-5 pt-24">
          <div className="text-center max-w-md">
            <h1 className="font-display text-3xl sm:text-4xl font-medium">
              Story not found.
            </h1>
            <p className="mt-4 text-[#44403C]">
              This journal entry doesn&apos;t exist (or has been removed).
            </p>
            <button
              onClick={() => navigate("/journal")}
              className="cta-pill cta-primary mt-6"
              data-testid="journal-notfound-back"
            >
              <ArrowLeft size={16} /> Back to Journal
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!story) {
    return (
      <div className="bg-bg text-[#1C1917] min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center pt-24">
          <p className="text-[#78716C]">Loading story…</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-bg text-[#1C1917]" data-testid={`journal-post-${story.slug}`}>
      <Header />

      <main className="pt-24 sm:pt-28">
        {/* Hero image */}
        {story.hero_image && (
          <div className="max-w-4xl mx-auto px-5 sm:px-8 mt-4">
            <div className="aspect-[16/9] rounded-2xl overflow-hidden">
              <img
                src={story.hero_image}
                alt={story.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}

        <article className="max-w-2xl mx-auto px-5 sm:px-8 mt-10 sm:mt-14">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-[10.5px] font-semibold tracking-[0.18em] uppercase text-[#9A3F25]">
              {story.tag}
            </span>
            <span className="text-[#A8A29E]">·</span>
            <span className="text-xs text-[#78716C]">{story.read_time}</span>
          </div>
          <h1
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1C1917]"
            style={{ lineHeight: 1.1 }}
            data-testid="journal-post-headline"
          >
            {story.title}
          </h1>

          <div className="mt-8 story-prose">
            {(story.body || []).map((block, i) => {
              if (block.type === "p") return <p key={i}>{block.text}</p>;
              if (block.type === "italic") return <p key={i} className="italic">{block.text}</p>;
              if (block.type === "h3") return <h2 key={i}>{block.text}</h2>;
              if (block.type === "quote")
                return <blockquote key={i}>{block.text}</blockquote>;
              if (block.type === "list")
                return (
                  <ul key={i}>
                    {(block.items || []).map((it, j) => (
                      <li key={j}>{it}</li>
                    ))}
                  </ul>
                );
              return null;
            })}
          </div>

          {story.survey_url && (
            <div
              className="mt-10 p-6 sm:p-7 rounded-2xl border border-[#F1D9C6] bg-[#FFF4EC]"
              data-testid="journal-post-survey-cta"
            >
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={16} className="text-[#EC6A4A]" />
                <span className="text-xs font-semibold tracking-[0.16em] uppercase text-[#9A3F25]">
                  Open · Takes 3 minutes
                </span>
              </div>
              <h3 className="font-display text-2xl text-[#1C1917]">
                Tell us what you&apos;re living.
              </h3>
              <a
                href={story.survey_url}
                target="_blank"
                rel="noopener noreferrer"
                className="cta-pill cta-accent mt-5"
              >
                Take the 3-min survey <ArrowUpRight size={16} />
              </a>
            </div>
          )}
        </article>

        {/* CTA — waitlist */}
        <section
          className="mt-16 sm:mt-24 py-16"
          style={{ backgroundColor: "var(--surface-alt)" }}
          data-testid="journal-post-cta"
        >
          <div className="max-w-2xl mx-auto px-5 sm:px-8">
            <h3
              className="font-display text-2xl sm:text-3xl font-medium text-[#1C1917]"
              style={{ lineHeight: 1.15 }}
            >
              Want to be there <em>when Supple opens?</em>
            </h3>
            <div className="mt-6 supple-card">
              <WaitlistFormCore testIdPrefix="journal-post-waitlist" />
            </div>
            <div className="mt-8 flex flex-wrap gap-6 justify-between text-sm">
              <Link
                to="/journal"
                className="text-[#9A3F25] font-semibold border-b border-[#9A3F25]/30 hover:border-[#9A3F25]"
                data-testid="journal-post-more-link"
              >
                ← More journal entries
              </Link>
              <Link
                to="/why"
                className="text-[#9A3F25] font-semibold border-b border-[#9A3F25]/30 hover:border-[#9A3F25]"
                data-testid="journal-post-why-link"
              >
                Why we&apos;re building this →
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
